import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Spinner,
  Button,
  Modal,
  Table,
  Form,
  Alert,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  CalendarEvent,
  CheckCircleFill,
  Clock,
  TelephoneFill,
  EnvelopePaperFill,
  Hash,
  Eye,
  FileEarmarkText,
  Download,
  PencilSquare,
  ExclamationTriangleFill,
  XCircleFill,
  QuestionCircleFill,
  ClipboardCheck,
  Calendar3,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_ACTIVITIES = `${API_BASE}/getActivitiesByUser`;
const ENDPOINT_TG_LIST = `${API_BASE}/getTGByList`;
const ENDPOINT_ACTIVITY_BY_ID = `${API_BASE}/getActivityById`;
const ENDPOINT_UPDATE_STATUS = `${API_BASE}/changeStatus`;
const ENDPOINT_TOGGLE_LEARN_NOTIFY = `${API_BASE}/toggleLearnNotify`;
const ENDPOINT_TOGGLE_DO_NOTIFY = `${API_BASE}/toggleDoNotify`;
const ENDPOINT_DOWNLOAD = `${API_BASE}/downloadSingleDocument`;
const ENDPOINT_AI_EACH = `${API_BASE}/analyzeEachDocument`;
const ENDPOINT_USER_CYCLE = `${API_BASE}/getUserCycle`;

function parseDateFlexible(input) {
  if (!input && input !== 0) return null;

  if (input instanceof Date) {
    const dt = new Date(input.getFullYear(), input.getMonth(), input.getDate());
    return isNaN(+dt) ? null : dt;
  }

  if (typeof input === "string") {
    const v = input.trim();
    if (!v) return null;

    let m = v.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) {
      const dt = new Date(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[3], 10),
      );
      return isNaN(+dt) ? null : dt;
    }

    m = v.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) {
      const dt = new Date(
        parseInt(m[3], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[1], 10),
      );
      return isNaN(+dt) ? null : dt;
    }

    const d2 = new Date(v);
    if (!isNaN(+d2)) {
      return new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
    }
  }

  return null;
}

function formatToDDMMYYYY(dateInput) {
  if (!dateInput) return "";
  const d = parseDateFlexible(dateInput) || new Date(dateInput);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

const ENDPOINT_DOWNLOAD_ALL = `${API_BASE}/downloadAttachments`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";

const DEFAULT_STATUS_MAP = {
  Pending: 2,
  Approved: 3,
  "Re-submit": 4,
  Rejected: 7,
};

function loadStatusMap() {
  try {
    const raw = process.env.REACT_APP_STATUS_IDS;
    if (!raw) return DEFAULT_STATUS_MAP;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATUS_MAP, ...parsed };
  } catch {
    return DEFAULT_STATUS_MAP;
  }
}

const STATUS_NAME_TO_ID = loadStatusMap();
const STATUS_ID_TO_NAME = Object.fromEntries(
  Object.entries(STATUS_NAME_TO_ID).map(([k, v]) => [String(v), k]),
);

function getStatusId(input) {
  if (input == null) return null;
  if (!isNaN(Number(input))) return Number(input);
  return STATUS_NAME_TO_ID[input] ?? null;
}

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";
  const userId = user.user_id ?? user.id ?? "";
  return { displayName, userId };
}

const fmtRange = (from, to) => {
  if (!from && !to) return "-";
  return `${from || "-"}${to ? ` - ${to}` : ""}`;
};

function toIntId(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeDocStatus(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  if (!s) return "";
  if (["correct", "valid", "ok", "pass", "passed", "check"].includes(s))
    return "correct";
  if (["warning", "warn", "partial", "needs_review"].includes(s))
    return "warning";
  if (["wrong", "invalid", "fail", "failed", "error"].includes(s))
    return "wrong";
  return s;
}

function docStatusMeta(statusRaw, isValid) {
  const s = normalizeDocStatus(statusRaw);

  if (s === "correct" || (s === "" && isValid === true)) {
    return {
      key: "correct",
      label: "Valid",
      icon: <CheckCircleFill size={14} />,
      bg: "rgba(16,185,129,.14)",
      text: "#065F46",
      border: "rgba(16,185,129,.25)",
    };
  }
  if (s === "warning") {
    return {
      key: "warning",
      label: "Incomplete",
      icon: <ExclamationTriangleFill size={14} />,
      bg: "rgba(245,158,11,.14)",
      text: "#92400E",
      border: "rgba(245,158,11,.28)",
    };
  }
  if (s === "wrong" || (s === "" && isValid === false)) {
    return {
      key: "wrong",
      label: "Invalid",
      icon: <XCircleFill size={14} />,
      bg: "rgba(239,68,68,.14)",
      text: "#7F1D1D",
      border: "rgba(239,68,68,.25)",
    };
  }
  return {
    key: s || "unknown",
    label: s ? s : "Unknown",
    icon: <QuestionCircleFill size={14} />,
    bg: "rgba(107,114,128,.14)",
    text: "#374151",
    border: "rgba(107,114,128,.25)",
  };
}

const STATUS_STYLES = {
  Pending: {
    bg: "rgba(245,158,11,.16)",
    text: "#B45309",
    icon: <Clock size={14} />,
  },
  Approved: {
    bg: "rgba(16,185,129,.16)",
    text: "#065F46",
    icon: <CheckCircleFill size={14} />,
  },
  Rejected: {
    bg: "rgba(239,68,68,.16)",
    text: "#7F1D1D",
    icon: <Clock size={14} />,
  },
  "Re-submit": {
    bg: "rgba(252, 39, 39, 0.16)",
    text: "#fc510eff",
    icon: <Clock size={14} />,
  },
};

const Chip = ({ label, tone = "Pending", className }) => {
  const s = STATUS_STYLES[tone] || STATUS_STYLES.Pending;
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: s.bg,
        color: s.text,
        borderRadius: 22,
        padding: "6px 12px",
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {s.icon}
      </span>
      {label}
    </span>
  );
};

const Pill = ({ label, ok = false, className }) => (
  <span
    className={className}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: ok ? "rgba(16,185,129,.16)" : "rgba(229,231,235,0.8)",
      color: ok ? "#065F46" : "#374151",
      borderRadius: 22,
      padding: "6px 12px",
      fontSize: 13,
      fontWeight: 700,
      whiteSpace: "nowrap",
    }}
  >
    {ok ? <CheckCircleFill size={14} /> : <Clock size={14} />}
    {label}
  </span>
);

const RegisteredMarkButton = ({ onClick, checked = false, title = "R" }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-pressed={checked}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      borderRadius: "50%",
      border: checked ? "1px solid #dc2626" : "1px solid rgba(0,0,0,.25)",
      background: checked ? "#dc2626" : "#fff",
      color: checked ? "#fff" : "#111827",
      fontSize: 13,
      fontWeight: 800,
      lineHeight: 1,
      padding: 0,
      cursor: "pointer",
      transition: "background .12s ease, border-color .12s ease",
    }}
  >
    R
  </button>
);

function IconActionButton({
  icon,
  title,
  onClick,
  disabled,
  loading,
  variant = "outline-secondary",
}) {
  return (
    <Button
      size="sm"
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      style={{
        width: 34,
        height: 34,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 10,
        flexShrink: 0,
      }}
    >
      {loading ? (
        <Spinner size="sm" animation="border" />
      ) : (
        <span style={{ display: "inline-flex" }}>{icon}</span>
      )}
    </Button>
  );
}

function pickRemarks(rowOrItem) {
  const r = rowOrItem || {};
  const raw = r.remarks ?? "";
  return String(raw || "").trim();
}

function shouldShowRemarks(statusName) {
  const s = String(statusName || "")
    .toLowerCase()
    .trim();
  return (
    s === "re-submit" || s === "resubmit" || s === "pending" || s === "rejected"
  );
}

function buildFilePathsFromDocs(docs) {
  if (!Array.isArray(docs)) return [];

  const out = [];

  for (const d of docs) {
    const docId = d?.id ?? d?.doc_id ?? d?.document_id ?? null;

    const raw =
      d?.path ||
      d?.file_path ||
      d?.filepath ||
      d?.server_path ||
      d?.full_path ||
      d?.absolute_path ||
      d?.local_path;

    if (typeof raw === "string" && raw.trim()) {
      out.push({ id: docId, path: raw.trim(), file_name: d?.file_name || "" });
      continue;
    }

    const base =
      d?.base_path || d?.folder_path || d?.dir || d?.directory || null;
    const fname = d?.file_name || d?.filename || null;

    if (
      typeof base === "string" &&
      typeof fname === "string" &&
      base &&
      fname
    ) {
      const joined = `${String(base).replace(/\/+$/, "")}/${String(
        fname,
      ).replace(/^\/+/, "")}`;
      if (joined.trim())
        out.push({ id: docId, path: joined.trim(), file_name: fname });
    }
  }

  const uniq = [];
  const seen = new Set();
  for (const x of out) {
    if (!x?.path) continue;
    if (!seen.has(x.path)) {
      seen.add(x.path);
      uniq.push(x);
    }
  }
  return uniq;
}

function AiDocResultModal({
  show,
  onHide,
  title,
  busy,
  err,
  results,
  docNameMap,
}) {
  const list = useMemo(() => {
    if (!results) return [];
    if (Array.isArray(results)) return results;
    return [results];
  }, [results]);

  const PillBadge = ({ ok, label }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        background: ok ? "rgba(16,185,129,.14)" : "rgba(239,68,68,.14)",
        color: ok ? "#065F46" : "#7F1D1D",
        fontWeight: 800,
        fontSize: 12,
        border: `1px solid ${
          ok ? "rgba(16,185,129,.25)" : "rgba(239,68,68,.25)"
        }`,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 99,
          background: ok ? "#10B981" : "#EF4444",
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );

  const MetaChip = ({ label, value }) => (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        background: "rgba(24,59,114,.08)",
        border: "1px solid rgba(24,59,114,.14)",
        color: "#183B72",
        fontWeight: 800,
        fontSize: 12,
        maxWidth: "100%",
      }}
      title={String(value || "")}
    >
      <span style={{ opacity: 0.75 }}>{label}:</span>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 320,
          display: "inline-block",
        }}
      >
        {value || "-"}
      </span>
    </div>
  );

  const StatusPill = ({ statusRaw, isValid }) => {
    const m = docStatusMeta(statusRaw, isValid);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 999,
          background: m.bg,
          color: m.text,
          border: `1px solid ${m.border}`,
          fontWeight: 900,
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
        title={`documentStatus: ${String(statusRaw || "-")}`}
      >
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {m.icon}
        </span>
        {m.label}
      </span>
    );
  };

  const VerifyChecklist = ({ verify }) => {
    const obj = verify && typeof verify === "object" ? verify : null;
    const keys = [
      "name",
      "nameOfActivity",
      "dateOfActivity",
      "typeOfActivity",
      "activityProvider",
    ];
    const labels = {
      name: "Name",
      nameOfActivity: "Name Of Activity",
      dateOfActivity: "Date of Activity",
      typeOfActivity: "Type of Activity",
      activityProvider: "Activity Provider",
    };

    if (!obj) return null;

    const items = keys
      .filter((k) => k in obj)
      .map((k) => ({ key: k, label: labels[k] || k, ok: !!obj[k] }));

    if (!items.length) return null;

    return (
      <div style={{ marginTop: 12 }}>
        <div
          className="d-flex align-items-center gap-2"
          style={{
            fontWeight: 900,
            fontSize: 12,
            color: "#374151",
            marginBottom: 6,
          }}
        >
          <ClipboardCheck /> Checklist
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 8,
            background: "rgba(17,24,39,.03)",
            border: "1px solid rgba(0,0,0,.08)",
            borderRadius: 14,
            padding: 10,
          }}
        >
          {items.map((it) => (
            <div
              key={it.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                borderRadius: 12,
                background: it.ok
                  ? "rgba(16,185,129,.06)"
                  : "rgba(239,68,68,.06)",
                border: `1px solid ${
                  it.ok ? "rgba(16,185,129,.14)" : "rgba(239,68,68,.14)"
                }`,
                fontSize: 13,
                fontWeight: 800,
                color: "#111827",
              }}
            >
              {it.ok ? (
                <CheckCircleFill size={16} color="#10B981" />
              ) : (
                <XCircleFill size={16} color="#EF4444" />
              )}
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getFileName = (docRes, idx) => {
    const fid = docRes?.fileId ?? docRes?.id ?? null;
    const fromMap = fid != null ? docNameMap?.[String(fid)] : "";
    return (
      fromMap ||
      docRes?.fileName ||
      docRes?.filename ||
      (list.length > 1 ? `Document ${idx + 1}` : "Document")
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="custom-font ai-result-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title || "AI Result"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {busy ? (
          <div className="py-3 text-muted">
            <Spinner size="sm" className="me-2" /> Analyzing…
          </div>
        ) : err ? (
          <Alert
            variant="danger"
            className="mb-0"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {err}
          </Alert>
        ) : list.length ? (
          <div style={{ display: "grid", gap: 12 }}>
            {list.map((one, idx) => {
              const docRes = one?.documents?.[0] || null;
              const issues = Array.isArray(docRes?.issues) ? docRes.issues : [];
              const hasIssues = issues.length > 0;

              const fileName = getFileName(docRes, idx);

              return (
                <div
                  key={docRes?.fileId || idx}
                  style={{
                    border: "1px solid rgba(0,0,0,.10)",
                    borderRadius: 16,
                    background: "#F8FAFC",
                    padding: 14,
                  }}
                >
                  <div
                    className="d-flex flex-wrap align-items-center justify-content-between"
                    style={{ gap: 10 }}
                  >
                    <div className="fw-bold d-flex align-items-center gap-2">
                      <span style={{ fontSize: 16 }}>🤖</span>
                      <span style={{ fontSize: 14 }}>{fileName}</span>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {!docRes?.isValid && (
                        <StatusPill
                          statusRaw={docRes?.documentStatus}
                          isValid={docRes?.isValid}
                        />
                      )}
                      <PillBadge
                        ok={!!docRes?.isReadable}
                        label={docRes?.isReadable ? "Readable" : "Not Readable"}
                      />
                      <PillBadge
                        ok={!!docRes?.isValid}
                        label={docRes?.isValid ? "Valid" : "Invalid"}
                      />
                    </div>
                  </div>

                  <div
                    className="d-flex flex-wrap align-items-center"
                    style={{ gap: 8, marginTop: 12 }}
                  >
                    <MetaChip label="File ID" value={docRes?.fileId || "-"} />
                    <MetaChip
                      label="Type"
                      value={docRes?.detectedType || "Unknown"}
                    />
                  </div>

                  <VerifyChecklist verify={docRes?.verify} />

                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        fontWeight: 900,
                        fontSize: 12,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      {hasIssues ? "Issues" : "Notes"}
                    </div>

                    <div
                      style={{
                        background: hasIssues
                          ? "rgba(239,68,68,.06)"
                          : "rgba(16,185,129,.06)",
                        border: hasIssues
                          ? "1px solid rgba(239,68,68,.14)"
                          : "1px solid rgba(16,185,129,.14)",
                        borderRadius: 14,
                        padding: 10,
                        fontSize: 13,
                        color: "#111827",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.45,
                      }}
                    >
                      {hasIssues
                        ? issues.map((x) => `• ${x}`).join("\n")
                        : "No issues detected."}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-muted">No result.</div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function DetailsModal({
  show,
  onHide,
  baseItem,
  module,
  onOpenChangeStatus,
  profile,
  onEdit,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);
  const [docs, setDocs] = useState([]);
  const [downloadingDocId, setDownloadingDocId] = useState(null);

  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [aiResults, setAiResults] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiTitle, setAiTitle] = useState("AI Result");

  const [downloadingAll, setDownloadingAll] = useState(false);
  const [viewingDocKey, setViewingDocKey] = useState(null);

  const docNameMap = useMemo(() => {
    const map = {};
    for (const d of docs || []) {
      const id = d?.id ?? d?.doc_id ?? d?.document_id ?? null;
      if (id != null && d?.file_name) map[String(id)] = d.file_name;
    }
    return map;
  }, [docs]);

  useEffect(() => {
    const run = async () => {
      if (!show || !baseItem?.id || !module) return;

      setBusy(true);
      setErr("");
      setRow(null);
      setDocs([]);

      setAiBusy(false);
      setAiErr("");
      setAiResults(null);
      setShowAiModal(false);
      setAiTitle("AI Result");

      setDownloadingAll(false);
      setDownloadingDocId(null);

      try {
        const token =
          localStorage.getItem("token") ||
          localStorage.getItem("authToken") ||
          localStorage.getItem("auth_token") ||
          "";

        const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
          params: { module, id: baseItem.id },
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          timeout: 20000,
        });

        const arr = Array.isArray(data?.data) ? data.data : [];
        setRow(arr[0] || null);
        setDocs(Array.isArray(data?.docs) ? data.docs : []);
      } catch (e) {
        setErr("Failed to load details.");
        setRow(null);
        setDocs([]);
      } finally {
        setBusy(false);
      }
    };
    run();
  }, [show, baseItem, module]);

  const getTokenHeader = () => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("auth_token") ||
      "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const buildMeta = () => {
    const name =
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
      [row?.first_name, row?.last_name].filter(Boolean).join(" ") ||
      profile?.display_name ||
      row?.display_name ||
      "Unknown";

    const dateOfActivity = fmtRange(
      row?.actDateFr || baseItem?.date_from,
      row?.actDateTo || baseItem?.date_to,
    );

    const typeOfActivity =
      (module === "learn"
        ? row?.typeOfActivity || baseItem?.type_text || ""
        : row?.typeOfActivity || "") || "";

    const activityProvider =
      row?.activityProvider ||
      row?.activity_provider ||
      baseItem?.activity_provider ||
      "";

    return { name, dateOfActivity, typeOfActivity, activityProvider };
  };

  const handleDownload = async (d) => {
    try {
      if (!d?.file_name) return;
      if (!row?.user_id) {
        alert("Owner not loaded yet. Reopen the details and try again.");
        return;
      }

      const docKey = d.id ?? d.doc_id ?? `${d.attachment_type}-${d.file_name}`;
      setDownloadingDocId(docKey);

      const idKey = module === "learn" ? "learnId" : "doId";
      const params = {
        [idKey]: String(baseItem.id),
        filename: d.file_name,
        isAdmin: "1",
        user_id: String(row.user_id),
      };

      const resp = await axios.get(ENDPOINT_DOWNLOAD, {
        params,
        responseType: "blob",
        headers: {
          ...getTokenHeader(),
          Accept: "application/octet-stream",
        },
        timeout: 60000,
      });

      const contentType = (resp.headers?.["content-type"] || "").toLowerCase();
      if (contentType.includes("application/json")) {
        const text = await new Response(resp.data).text();
        try {
          const j = JSON.parse(text);
          throw new Error(j?.message || "Download failed.");
        } catch {
          throw new Error("Download failed.");
        }
      }

      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = d.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Download failed:", e);
      alert(e?.message || "Download failed. Check file path and permissions.");
    } finally {
      setDownloadingDocId(null);
    }
  };

  const guessMime = (filename = "", headerType = "") => {
    const ct = String(headerType || "").toLowerCase();

    if (ct && !ct.includes("application/octet-stream")) return ct;

    const ext = String(filename).toLowerCase().split(".").pop();
    if (ext === "pdf") return "application/pdf";
    if (ext === "png") return "image/png";
    if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
    if (ext === "gif") return "image/gif";
    if (ext === "webp") return "image/webp";
    if (ext === "txt") return "text/plain";
    return "application/octet-stream";
  };

  const handleViewFile = async (d) => {
    const previewWin = window.open("about:blank", "_blank");

    if (!previewWin) {
      alert(
        "Popup blocked. Please allow popups for this site to preview documents.",
      );
      return;
    }

    try {
      previewWin.opener = null;
    } catch {}

    try {
      if (!d?.file_name) {
        previewWin.close();
        return;
      }

      if (!row?.user_id) {
        previewWin.close();
        alert("Owner not loaded yet. Reopen the details and try again.");
        return;
      }

      const docId = d?.id ?? d?.doc_id ?? d?.document_id ?? null;
      const docKey = docId ?? `${d.attachment_type}-${d.file_name}`;
      setViewingDocKey(docKey);

      try {
        previewWin.document.title = "Opening…";
        previewWin.document.body.innerHTML = `
        <div style="font-family:Arial;padding:16px">
          Loading preview… please wait
        </div>`;
      } catch {}

      const idKey = module === "learn" ? "learnId" : "doId";
      const params = {
        [idKey]: String(baseItem.id),
        filename: d.file_name,
        isAdmin: "1",
        user_id: String(row.user_id),
      };

      const resp = await axios.get(ENDPOINT_DOWNLOAD, {
        params,
        responseType: "blob",
        headers: { ...getTokenHeader(), Accept: "*/*" },
        timeout: 60000,
      });

      const contentType = (resp.headers?.["content-type"] || "").toLowerCase();

      if (contentType.includes("application/json")) {
        const text = await new Response(resp.data).text();
        let msg = "View failed.";
        try {
          const j = JSON.parse(text);
          msg = j?.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const mime = guessMime(d.file_name, contentType);

      const canPreview =
        mime.includes("pdf") ||
        mime.startsWith("image/") ||
        mime.startsWith("text/");

      if (!canPreview) {
        try {
          previewWin.document.title = "Preview not available";
          previewWin.document.body.innerHTML = `
          <div style="font-family:Arial;padding:16px">
            <h3 style="margin:0 0 8px">Preview not available</h3>
            <div>This file type (<b>${mime}</b>) cannot be previewed in browser.</div>
            <div style="margin-top:10px">Please use <b>Download</b> instead.</div>
          </div>`;
        } catch {}
        return;
      }

      const blobUrl = window.URL.createObjectURL(
        new Blob([resp.data], { type: mime }),
      );

      previewWin.location.href = blobUrl;

      setTimeout(
        () => {
          try {
            window.URL.revokeObjectURL(blobUrl);
          } catch {}
        },
        10 * 60 * 1000,
      ); // 10 minutes
    } catch (e) {
      console.error("View failed:", e);
      try {
        previewWin.document.title = "Failed";
        previewWin.document.body.innerHTML = `
        <div style="font-family:Arial;padding:16px;color:#b91c1c">
          Failed to open preview.<br/>
          ${String(e?.message || "Unknown error")}
        </div>`;
      } catch {
        alert(e?.message || "View failed.");
      }
    } finally {
      setViewingDocKey(null);
    }
  };

  const handleDownloadAll = async () => {
    try {
      if (!row?.user_id) {
        alert("Owner not loaded yet. Reopen the details and try again.");
        return;
      }

      setDownloadingAll(true);

      const idKey = module === "learn" ? "learnId" : "doId";
      const params = {
        [idKey]: String(baseItem.id),
        isAdmin: "1",
        user_id: String(row.user_id),
      };

      const resp = await axios.get(ENDPOINT_DOWNLOAD_ALL, {
        params,
        responseType: "blob",
        headers: {
          ...getTokenHeader(),
          Accept: "application/octet-stream",
        },
        timeout: 180000,
      });

      const contentType = (resp.headers?.["content-type"] || "").toLowerCase();

      if (contentType.includes("application/json")) {
        const text = await new Response(resp.data).text();
        try {
          const j = JSON.parse(text);
          throw new Error(j?.message || "Download failed.");
        } catch {
          throw new Error("Download failed.");
        }
      }

      const cd = resp.headers?.["content-disposition"] || "";
      let filename = "";
      const m = /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i.exec(cd);
      if (m && m[1])
        filename = decodeURIComponent(m[1].replace(/"/g, "").trim());

      if (!filename) {
        filename =
          module === "learn"
            ? `learn-${baseItem.id}.zip`
            : `do-${baseItem.id}.zip`;
      }

      const blobUrl = window.URL.createObjectURL(
        new Blob([resp.data], {
          type: contentType || "application/octet-stream",
        }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Download all failed:", e);
      alert(
        e?.response?.data?.message ||
          e?.message ||
          "Download all failed. Check folder path and permissions.",
      );
    } finally {
      setDownloadingAll(false);
    }
  };

  const callAnalyzeEachDocument = async ({ filePaths, title }) => {
    const meta = buildMeta();

    const missingId = filePaths.filter((x) => !x?.id);
    const missingPath = filePaths.filter((x) => !x?.path);

    if (missingId.length) {
      throw new Error(
        "Some documents have missing doc id. analyzeEachDocument requires filePaths: [{ id, path }]. Please ensure getActivityById returns doc.id.",
      );
    }
    if (missingPath.length) {
      throw new Error(
        "Some documents have missing server path. Ensure getActivityById returns doc.path / doc.file_path / doc.server_path.",
      );
    }

    const payload = {
      module,
      id: String(baseItem?.id || ""),
      name: meta.name,
      dateOfActivity: meta.dateOfActivity,
      typeOfActivity: meta.typeOfActivity,
      activityProvider: meta.activityProvider,
      filePaths: filePaths.map((x) => ({
        id: Number(x.id),
        path: String(x.path),
      })),
    };

    setAiTitle(title || "AI Result");
    setShowAiModal(true);
    setAiBusy(true);
    setAiErr("");
    setAiResults(null);

    try {
      const { data } = await axios.post(ENDPOINT_AI_EACH, payload, {
        headers: { "Content-Type": "application/json", ...getTokenHeader() },
        timeout: 180000,
      });

      if (!data?.success)
        throw new Error(data?.message || "AI analysis failed.");

      const arr = Array.isArray(data?.data) ? data.data : [];
      setAiResults(arr);

      try {
        const { data: refreshed } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
          params: { module, id: baseItem.id },
          headers: { "Content-Type": "application/json", ...getTokenHeader() },
          timeout: 20000,
        });
        setDocs(Array.isArray(refreshed?.docs) ? refreshed.docs : docs);
      } catch {
        // ignore
      }
    } catch (e) {
      setAiErr(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to generate AI analysis.",
      );
    } finally {
      setAiBusy(false);
    }
  };

  const handleAiAnalyzeAll = async () => {
    try {
      const built = buildFilePathsFromDocs(docs);
      const filePaths = built
        .map((x) => ({ id: x.id, path: x.path }))
        .filter((x) => x.id && x.path);

      if (!filePaths.length) {
        throw new Error(
          "No valid documents found to analyze.\n\nMake sure docs list includes doc.id and doc.path (server path).",
        );
      }

      await callAnalyzeEachDocument({
        filePaths,
        title: "AI Result — All Documents",
      });
    } catch (e) {
      setAiTitle("AI Result — All Documents");
      setShowAiModal(true);
      setAiBusy(false);
      setAiResults(null);
      setAiErr(e?.message || "Unable to analyze all documents.");
    }
  };

  const handleAiAnalyzeDoc = async (doc) => {
    try {
      const fp = buildFilePathsFromDocs([doc])[0];
      const docId = fp?.id;
      const p = fp?.path;

      if (!docId) {
        throw new Error(
          "Missing doc id in docs list (required by analyzeEachDocument). Please return doc.id from backend.",
        );
      }
      if (!p) {
        throw new Error(
          "Missing server file path. Ensure getActivityById returns doc.path / doc.file_path / doc.server_path.",
        );
      }

      await callAnalyzeEachDocument({
        filePaths: [{ id: docId, path: p }],
        title: `AI Result — ${doc?.file_name || "Document"}`,
      });
    } catch (e) {
      setAiTitle(`AI Result — ${doc?.file_name || "Document"}`);
      setShowAiModal(true);
      setAiBusy(false);
      setAiResults(null);
      setAiErr(e?.message || "Unable to analyze this document.");
    }
  };

  const statusText = row?.status_name ?? baseItem?.status_name ?? "Pending";
  const isEditDisabled = ["rejected"].includes(
    String(statusText || "")
      .trim()
      .toLowerCase(),
  );
  const remarksText = pickRemarks(row) || pickRemarks(baseItem) || ""; // comment / remarks

  const tgName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    [row?.first_name, row?.last_name].filter(Boolean).join(" ") ||
    profile?.display_name ||
    row?.display_name ||
    "-";

  const tgBadgeNo =
    profile?.badge_no || row?.badge_no || baseItem?.badge_no || "-";

  const headerRows =
    module === "learn"
      ? [
          ["Name / Badge No", `${tgName} (${tgBadgeNo})`],
          ["Module", "LEARN"],
          [
            "Date Range",
            fmtRange(
              row?.actDateFr || baseItem?.date_from,
              row?.actDateTo || baseItem?.date_to,
            ),
          ],
          ["Title", row?.activity_title || baseItem?.title || "-"],
          ["Type", row?.typeOfActivity || baseItem?.type_text || "-"],
          ["Category", row?.category || baseItem?.category_text || "-"],
          ["Status", statusText],

          ...(shouldShowRemarks(statusText) && remarksText
            ? [["Comment", remarksText]]
            : []),

          [
            "Activity Provider",
            row?.activityProvider || row?.activity_provider || "-",
          ],

          [
            "Classroom Course Docs",
            String(row?.ctrCDDocs ?? baseItem?.ctrCDDocs ?? 0),
          ],
          [
            "Virtual Lecture Docs",
            String(row?.ctrVLDocs ?? baseItem?.ctrVLDocs ?? 0),
          ],
          [
            "Site-specific training Docs",
            String(row?.ctrSSTDocs ?? baseItem?.ctrSSTDocs ?? 0),
          ],
          [
            "Proof of Participation Docs",
            String(row?.ctrPPDocs ?? baseItem?.ctrPPDocs ?? 0),
          ],
        ]
      : [
          ["Name / Badge No", `${tgName} (${tgBadgeNo})`],
          ["Module", "DO"],
          [
            "Date Range",
            fmtRange(
              row?.actDateFr || baseItem?.date_from,
              row?.actDateTo || baseItem?.date_to,
            ),
          ],
          ["Status", statusText],

          ...(shouldShowRemarks(statusText) && remarksText
            ? [["Comment", remarksText]]
            : []),

          [
            "Conducted Tour",
            baseItem?.tour_conducted || Number(row?.ctrPCDocs || 0) > 0
              ? "Yes"
              : "No",
          ],

          [
            "Proof of Remuneration Docs",
            String(row?.ctrPNUMDocs ?? baseItem?.ctrPNUMDocs ?? 0),
          ],
          [
            "Proof of Tour Conducted Docs",
            String(row?.ctrPCDocs ?? baseItem?.ctrPCDocs ?? 0),
          ],
        ];

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="xl"
        className={`custom-font learn-details-modal ${
          showAiModal ? "blurred-behind" : ""
        }`}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {(module === "learn" ? "LEARN" : "DO") + " — Details"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {busy && (
            <div className="mb-3">
              <Spinner size="sm" className="me-2" /> Loading details…
            </div>
          )}
          {err && (
            <Alert variant="danger" className="mb-3">
              {err}
            </Alert>
          )}

          <div
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: 14,
              overflow: "hidden",
              marginBottom: 16,
            }}
          >
            <Table bordered responsive size="sm" className="mb-0">
              <tbody>
                {headerRows.map(([k, v]) => (
                  <tr key={k}>
                    <th
                      style={{
                        width: 260,
                        background: "#F8FAFC",
                        color: "#1F2937",
                        fontWeight: 700,
                        fontSize: 13,
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      {k}
                    </th>
                    <td style={{ border: "1px solid #E5E7EB" }}>
                      {k === "Status" ? (
                        <Chip label={v} tone={v} />
                      ) : (
                        v
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div
            className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
            style={{
              background: "#F8FAFC",
              border: "1px solid #E5E7EB",
              borderRadius: 14,
              padding: "12px 16px",
              marginBottom: 8,
            }}
          >
            <div className="fw-bold d-flex align-items-center gap-2">
              <span style={{ fontSize: 16 }}>🤖</span> Analysis with AI (All
              Files)
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleAiAnalyzeAll}
                disabled={aiBusy || busy || !!err || downloadingAll}
                style={{ borderRadius: 10 }}
              >
                {aiBusy ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Analyzing…
                  </>
                ) : (
                  "Analyze All"
                )}
              </Button>

              <Button
                size="sm"
                variant="outline-success"
                onClick={handleDownloadAll}
                disabled={busy || !!err || aiBusy || downloadingAll}
                title="Download all files (zip if multiple)"
                style={{ borderRadius: 10 }}
              >
                {downloadingAll ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Downloading…
                  </>
                ) : (
                  <>
                    <Download size={14} className="me-1" />
                    Download All
                  </>
                )}
              </Button>

              <Button
                size="sm"
                variant={isEditDisabled ? "secondary" : "warning"}
                onClick={() => onEdit?.(baseItem, module)}
                disabled={
                  busy || !!err || aiBusy || downloadingAll || isEditDisabled
                }
                title={
                  isEditDisabled
                    ? "Edit is disabled for Rejected records"
                    : "Edit"
                }
                style={{ borderRadius: 10 }}
              >
                <PencilSquare size={14} className="me-1" />
                Edit
              </Button>
            </div>
          </div>

          <hr className="my-3" />

          <div className="d-flex align-items-center gap-2 mb-2">
            <FileEarmarkText />
            <div className="fw-bold">Documents</div>
          </div>

          {docs.length ? (
            <Table bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Attachment Type</th>
                  <th>File Name</th>
                  <th style={{ width: 170 }}>Uploaded Date</th>
                  <th style={{ width: 170 }}>AI Verified Status</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const formatDate = (dateStr) => {
                    if (!dateStr) return "-";

                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) return dateStr;

                    const dd = String(date.getDate()).padStart(2, "0");
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const yyyy = date.getFullYear();

                    let hours = date.getHours();
                    const minutes = String(date.getMinutes()).padStart(2, "0");

                    const ampm = hours >= 12 ? "PM" : "AM";
                    hours = hours % 12;
                    hours = hours ? hours : 12; // convert 0 → 12

                    const hh = String(hours).padStart(2, "0");

                    return `${dd}-${mm}-${yyyy} ${hh}:${minutes} ${ampm}`;
                  };

                  const typeMap = {
                    ccNoOfAttach: "Classroom Courses",
                    vlAttached: "Physical / Virtual Lectures",
                    sstAttached: "Site-specific Training",
                    ppAttached: "Proof of Participation",
                    remNoOfAttach: "Proof of Remuneration",
                    conNoOfAttach: "Proof of Tour Conducted",
                  };

                  const grouped = docs.reduce((acc, d) => {
                    const key = d.attachment_type || "Unknown";
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(d);
                    return acc;
                  }, {});

                  const StatusBadge = ({ d }) => {
                    const m = docStatusMeta(d?.document_status, d?.isValid);
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: m.bg,
                          color: m.text,
                          border: `1px solid ${m.border}`,
                          fontWeight: 900,
                          fontSize: 12,
                          whiteSpace: "nowrap",
                        }}
                        title={
                          d?.document_status
                            ? `document_status: ${d.document_status}`
                            : `isValid: ${String(d?.isValid)}`
                        }
                      >
                        {m.icon} {m.label}
                      </span>
                    );
                  };

                  return Object.entries(grouped).flatMap(([type, list]) => {
                    const displayType = typeMap[type] || type;
                    const rowspan = list.length;

                    return list.map((d, idx) => {
                      const docId =
                        d?.id ?? d?.doc_id ?? d?.document_id ?? null;
                      const docKey =
                        docId ?? `${d.attachment_type}-${d.file_name}`;
                      const isThisDownloading = downloadingDocId === docKey;

                      return (
                        <tr key={docKey}>
                          {idx === 0 && (
                            <td
                              rowSpan={rowspan}
                              style={{
                                verticalAlign: "top",
                                fontWeight: "600",
                                background: "#F8FAFC",
                              }}
                            >
                              {displayType}
                            </td>
                          )}
                          <td style={{ verticalAlign: "top" }}>
                            {d.file_name}
                          </td>
                          <td style={{ verticalAlign: "top" }}>
                            {formatDate(d.uploaded_date)}
                          </td>

                          <td style={{ verticalAlign: "top" }}>
                            <StatusBadge d={d} />
                          </td>

                          <td style={{ verticalAlign: "top" }}>
                            <div className="d-flex flex-nowrap gap-2">
                              <IconActionButton
                                icon={<Eye size={15} />}
                                title="View in browser"
                                variant="outline-secondary"
                                loading={viewingDocKey === docKey}
                                onClick={() => handleViewFile(d)}
                                disabled={
                                  busy ||
                                  !!err ||
                                  aiBusy ||
                                  downloadingAll ||
                                  !!downloadingDocId ||
                                  !!viewingDocKey
                                }
                              />

                              <IconActionButton
                                icon={<Download size={15} />}
                                title="Download"
                                variant="outline-success"
                                loading={isThisDownloading}
                                onClick={() => handleDownload(d)}
                                disabled={
                                  !!downloadingDocId ||
                                  downloadingAll ||
                                  !!viewingDocKey
                                }
                              />

                              <IconActionButton
                                icon={<span>🤖</span>}
                                title="Analyze this document with AI"
                                variant="outline-primary"
                                onClick={() => handleAiAnalyzeDoc(d)}
                                disabled={
                                  busy ||
                                  !!err ||
                                  !!downloadingDocId ||
                                  !!viewingDocKey ||
                                  aiBusy ||
                                  downloadingAll
                                }
                              />
                            </div>

                            {!docId ? (
                              <div
                                className="text-danger mt-2"
                                style={{ fontSize: 12 }}
                              >
                                Missing <b>doc id</b> in docs list (required by
                                analyzeEachDocument).
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      );
                    });
                  });
                })()}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted">No documents.</div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button
              variant="outline-secondary"
              onClick={onHide}
              className="me-2"
              style={{ borderRadius: 10, minWidth: 90 }}
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={onOpenChangeStatus}
              style={{ borderRadius: 10, minWidth: 130 }}
            >
              Change Status
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <AiDocResultModal
        show={showAiModal}
        onHide={() => setShowAiModal(false)}
        title={aiTitle}
        busy={aiBusy}
        err={aiErr}
        results={aiResults}
        docNameMap={docNameMap}
      />
    </>
  );
}

function ChangeStatusModal({ show, onHide, currentStatus, onSubmit, busy }) {
  const [status, setStatus] = useState(currentStatus || "Pending");
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    setStatus(currentStatus || "Pending");
    setRemarks("");
  }, [currentStatus, show]);

  return (
    <Modal show={show} onHide={onHide} className="custom-font" centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Status</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">New status</Form.Label>
          <Form.Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={busy}
          >
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Re-submit">Re-submit</option>
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-bold">Remarks (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Add remarks for the user (included in the email)…"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            disabled={busy}
          />
          <Form.Text className="text-muted">
            For <b>Re-submit</b> or <b>Rejected</b>, include clear instructions.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => onSubmit({ status, remarks })}
          disabled={busy}
        >
          {busy ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" /> Saving…
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RemarksBox({ statusName, remarks }) {
  const [expanded, setExpanded] = useState(false);
  const text = String(remarks || "").trim();
  if (!shouldShowRemarks(statusName)) return null;
  if (!text) return null;

  const isRejected = String(statusName || "")
    .toLowerCase()
    .includes("reject");

  return (
    <div
      style={{
        marginTop: 10,
        background: isRejected ? "rgba(239,68,68,.06)" : "rgba(245,158,11,.08)",
        border: `1px solid ${
          isRejected ? "rgba(239,68,68,.18)" : "rgba(245,158,11,.22)"
        }`,
        borderRadius: 12,
        padding: "10px 12px",
        color: "#111827",
      }}
    >
      <div
        className="d-flex align-items-center gap-2"
        style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}
      >
        {isRejected ? (
          <XCircleFill size={15} color="#EF4444" />
        ) : (
          <ExclamationTriangleFill size={15} color="#F59E0B" />
        )}
        <span>{isRejected ? "Rejected Comment" : "Re-submit Comment"}</span>
      </div>
      <div
        style={{
          fontSize: 13,
          whiteSpace: "pre-wrap",
          lineHeight: 1.45,
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
        }}
      >
        {text}
      </div>
      {text.length > 110 ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "none",
            border: 0,
            padding: 0,
            marginTop: 4,
            fontSize: 12,
            fontWeight: 600,
            color: "#1D4ED8",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

function displayCardDate(v) {
  return v && String(v).trim() ? v : "-";
}

const CARD_FRAME_STYLE = {
  background: "white",
  borderRadius: 14,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "#E5E7EB",
  padding: "16px 18px",
  marginBottom: 12,
  transition: "background-color .15s ease, border-color .15s ease, box-shadow .15s ease",
};

const CARD_FRAME_HOVER_STYLE = {
  background: "#F3F4F6",
  borderColor: "#CBD5E1",
  boxShadow: "0 2px 8px rgba(15,23,42,.08)",
};

function CardFrame({ children }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...CARD_FRAME_STYLE, ...(hover ? CARD_FRAME_HOVER_STYLE : {}) }}
    >
      {children}
    </div>
  );
}

const ELLIPSIS_STYLE = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function CardHeader({ item, statusName, onToggleNotify }) {
  return (
    <div className="d-flex align-items-center" style={{ gap: 10 }}>
      <div
        className="d-flex align-items-center"
        style={{ gap: 8, color: "#0f172a", fontSize: 14, fontWeight: 700, minWidth: 0 }}
      >
        <CalendarEvent size={15} color="#64748B" />
        <span style={ELLIPSIS_STYLE}>{fmtRange(item.date_from, item.date_to)}</span>
      </div>
      <div className="ms-auto d-flex align-items-center flex-shrink-0" style={{ gap: 8 }}>
        <RegisteredMarkButton
          checked={!!item.notify}
          onClick={() => onToggleNotify?.(item)}
          title={
            item.notify
              ? "Notify: on (click to turn off)"
              : "Notify: off (click to turn on)"
          }
        />
        <Chip label={statusName} tone={statusName} />
      </div>
    </div>
  );
}

function CardFooter({ item, onView, module }) {
  const dates = [
    ["Created", item.createdDate],
    ["Updated", item.updatedDate],
    ["Verified", item.verifiedDate],
  ];
  return (
    <div
      className="d-flex flex-wrap align-items-end justify-content-between"
      style={{ gap: 10, marginTop: 10, paddingTop: 8, borderTop: "1px solid #EEF2F6" }}
    >
      <div className="d-flex flex-wrap" style={{ gap: "6px 22px" }}>
        {dates.map(([label, value]) => (
          <div key={label}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: "#1F2937",
              }}
            >
              {label}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
              {displayCardDate(value)}
            </div>
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="outline-primary"
        className="flex-shrink-0"
        onClick={() => onView(item, module)}
      >
        <Eye size={14} className="me-1" /> View
      </Button>
    </div>
  );
}

function LearnCard({ item, onView, onToggleNotify }) {
  const statusName = item.status_name || "Pending";
  const remarks = pickRemarks(item);

  return (
    <CardFrame>
      <CardHeader item={item} statusName={statusName} onToggleNotify={onToggleNotify} />

      <div
        title={item.title || ""}
        style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginTop: 8, ...ELLIPSIS_STYLE }}
      >
        {item.title || "-"}
      </div>
      <div style={{ fontSize: 12, color: "#1F2937", marginTop: 2, ...ELLIPSIS_STYLE }}>
        {item.type_text || "-"} &middot; {item.category_text || "-"}
      </div>

      <CardFooter item={item} onView={onView} module="learn" />
      <RemarksBox statusName={statusName} remarks={remarks} />
    </CardFrame>
  );
}

function DoCard({ item, onView, onToggleNotify }) {
  const statusName = item.status_name || "Pending";
  const remarks = pickRemarks(item);

  return (
    <CardFrame>
      <CardHeader item={item} statusName={statusName} onToggleNotify={onToggleNotify} />

      <div style={{ marginTop: 8 }}>
        <Pill label="Conducted Tour" ok={!!item.tour_conducted} />
      </div>

      <CardFooter item={item} onView={onView} module="do" />
      <RemarksBox statusName={statusName} remarks={remarks} />
    </CardFrame>
  );
}

function getExpiryTone(expiry) {
  const fallback = {
    bg: "#F8FAFC",
    border: "#E5E7EB",
    color: "#0f172a",
    label: "",
  };
  if (!expiry) return fallback;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return fallback;

  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days < 0)
    return {
      bg: "#FEF2F2",
      border: "#FECACA",
      color: "#B91C1C",
      label: "Expired",
    };
  if (days <= 60)
    return {
      bg: "#FFFBEB",
      border: "#FDE68A",
      color: "#B45309",
      label: `${days} day${days === 1 ? "" : "s"} left`,
    };
  return {
    bg: "#ECFDF5",
    border: "#A7F3D0",
    color: "#047857",
    label: "Valid",
  };
}

const toTitleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/(^|[\s\-'])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

function ProfileInfoBar({ info }) {
  const nameParts = [info?.first_name, info?.last_name].filter(Boolean);
  const username = nameParts.length ? toTitleCase(nameParts.join(" ")) : "-";
  const email = info?.username || "-";
  const phone = [info?.country_code, info?.phone_number]
    .filter(Boolean)
    .join(" ");
  const expiry = info?.tg_expiry_date || "-";
  const badge = info?.badge_no || "-";
  const expiryTone = getExpiryTone(info?.tg_expiry_date);

  return (
    <div
      className="p-3 mb-3"
      style={{
        background: "white",
        borderRadius: 16,
        boxShadow: "0 2px 10px rgba(31,35,40,.08)",
        border: "1px solid rgba(0,0,0,.06)",
      }}
    >
      <div
        className="d-grid align-items-center"
        style={{ gridTemplateColumns: "200px 1fr", gap: 12 }}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            background: expiryTone.bg,
            border: `1px solid ${expiryTone.border}`,
            borderRadius: 12,
            minHeight: 64,
          }}
        >
          <div className="text-center">
            <div
              className="text-muted"
              style={{ fontSize: 12, letterSpacing: 0.3 }}
            >
              TG Expiry Date
            </div>
            <div
              style={{ fontSize: 20, fontWeight: 800, color: expiryTone.color }}
            >
              {expiry ? new Date(expiry).toLocaleDateString("en-GB") : ""}
            </div>
            {expiryTone.label && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: expiryTone.color,
                }}
              >
                {expiryTone.label}
              </div>
            )}
          </div>
        </div>

        <div
          className="d-flex flex-wrap align-items-center"
          style={{ gap: 30 }}
        >
          <div className="d-flex align-items-center gap-2">
            <PersonCircle size={18} color="#1A3584" />
            <div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Username
              </div>
              <div style={{ fontWeight: 700 }}>{username}</div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <EnvelopePaperFill size={18} color="#1A3584" />
            <div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Email
              </div>
              <div style={{ fontWeight: 700 }}>{email}</div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <TelephoneFill size={16} color="#1A3584" />
            <div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Phone
              </div>
              <div style={{ fontWeight: 700 }}>{phone || "-"}</div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Hash size={16} color="#1A3584" />
            <div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Badge No.
              </div>
              <div style={{ fontWeight: 700 }}>{badge}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColumnHeader({ label, count }) {
  return (
    <div
      className="d-flex align-items-center justify-content-between mb-3"
      style={{ paddingBottom: 10, borderBottom: "2px solid #183B72" }}
    >
      <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
        {label === "LEARN" ? "Learn" : "Do"} activities
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#334155",
          background: "#F1F5F9",
          borderRadius: 999,
          padding: "3px 12px",
        }}
      >
        {count} {count === 1 ? "item" : "items"}
      </span>
    </div>
  );
}

function getTransitionRows(rawPlan) {
  if (!rawPlan) return [];
  if (Array.isArray(rawPlan)) return rawPlan;
  if (Array.isArray(rawPlan.cycles)) return rawPlan.cycles;
  if (Array.isArray(rawPlan.transitionPlan)) return rawPlan.transitionPlan;
  if (Array.isArray(rawPlan.plan)) return rawPlan.plan;
  return [];
}

function mapTransitionRows(rawPlan) {
  return getTransitionRows(rawPlan).map((item, index) => {
    const end =
      item.cycle_end ||
      item.cycleEnd ||
      item.submission_end ||
      item.end ||
      item.endDate ||
      item.submissionEnds ||
      "";

    const learnReq = Number(
      item.learnReq ?? item.no_learn ?? item.learn ?? item.learn_required ?? 0,
    );
    const doReq = Number(
      item.doReq ?? item.no_do ?? item.do ?? item.do_required ?? 0,
    );

    const statusRaw = String(item.status || "").toLowerCase();
    let statusText = item.status || "Upcoming";
    let statusBg = "#e2e3e5";
    let statusColor = "#212529";

    if (statusRaw === "ongoing") {
      statusText = "Ongoing";
      statusBg = "#0d6efd";
      statusColor = "#fff";
    } else if (statusRaw === "completed" || statusRaw === "closed") {
      statusText = "Completed";
      statusBg = "#212529";
      statusColor = "#fff";
    } else {
      statusText = "Upcoming";
      statusBg = "#e2e3e5";
      statusColor = "#212529";
    }

    return {
      key: `${item.seq ?? index + 1}-${item.id ?? end}`,
      no: item.seq ?? index + 1,
      endFmt: formatToDDMMYYYY(end),
      learnReq,
      doReq,
      statusText,
      statusBg,
      statusColor,
    };
  });
}

function EmptyColumnState({ label }) {
  return (
    <div className="text-center py-5" style={{ color: "#1F2937" }}>
      <CheckCircleFill size={28} color="#34D399" className="mb-2" />
      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
        No {label} items to review
      </div>
      <div style={{ fontSize: 12 }}>You're all caught up here.</div>
    </div>
  );
}

export default function LearnDoBoard() {
  const { displayName, userId: localUserId } = useMemo(getStoredUser, []);
  const navigate = useNavigate();
  const location = useLocation();

  const stateUserId = location.state?.userId || null;
  const stateYear = location.state?.year || null;

  const qs = new URLSearchParams(location.search);
  const qsUserId = qs.get("userId");
  const qsYear = qs.get("year");

  const effectiveUserId = stateUserId || qsUserId || localUserId || "";
  const [year] = useState(
    String(stateYear || qsYear || new Date().getFullYear()),
  );

  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [learn, setLearn] = useState([]);
  const [doList, setDoList] = useState([]);

  const matchesStatus = (it) =>
    statusFilter === "All" ||
    String(it.status_name || "").toLowerCase() === statusFilter.toLowerCase();
  const visibleLearn = learn.filter(matchesStatus);
  const visibleDo = doList.filter(matchesStatus);
  const statusCount = (name) =>
    [...learn, ...doList].filter(
      (it) =>
        name === "All" ||
        String(it.status_name || "").toLowerCase() === name.toLowerCase(),
    ).length;
  const [err, setErr] = useState("");

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileErr, setProfileErr] = useState("");
  const [profile, setProfile] = useState(null);

  const [cycleLoading, setCycleLoading] = useState(false);
  const [cycleErr, setCycleErr] = useState("");
  const [cycleInfo, setCycleInfo] = useState(null);

  const [showDetails, setShowDetails] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [detailsModule, setDetailsModule] = useState(null);

  const [showChange, setShowChange] = useState(false);
  const [changeBusy, setChangeBusy] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const fetchUserInfo = async () => {
    setProfileLoading(true);
    setProfileErr("");

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      if (!effectiveUserId) {
        setProfileErr("User ID not found.");
        setProfile(null);
        return;
      }

      const { data } = await axios.get(ENDPOINT_TG_LIST, {
        params: {
          type: "detail",
          isAdmin: null,
          userId: ` AND u.user_id = ${effectiveUserId}`,
        },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      const rows = Array.isArray(data?.data) ? data.data : [];

      setProfile(rows[0] || null);
    } catch (e) {
      setProfileErr("Failed to load user info.");
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchCycleInfo = async () => {
    if (!effectiveUserId) return;

    setCycleLoading(true);
    setCycleErr("");

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const { data } = await axios.get(ENDPOINT_USER_CYCLE, {
        params: { userId: effectiveUserId },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      const obj = data?.data || null;
      if (!data?.success || !obj) {
        setCycleErr(data?.message || "Unable to load transition plan.");
        setCycleInfo(null);
        return;
      }

      setCycleInfo({
        transitionRows: mapTransitionRows(obj.transitionPlan),
      });
    } catch (e) {
      setCycleErr("Failed to load LEARN + DO cycle.");
      setCycleInfo(null);
    } finally {
      setCycleLoading(false);
    }
  };

  const handleEdit = (item, module) => {
    if (!module) return;

    if (module === "learn") {
      const learnId = item?.learn_id || item?.id;
      const ownerUserId =
        item?.user_id ||
        item?.userId ||
        detailsItem?.user_id ||
        detailsItem?.userId ||
        effectiveUserId;

      if (!learnId) {
        alert("Missing learn id.");
        return;
      }

      if (!ownerUserId) {
        alert("Missing user id.");
        return;
      }

      setShowDetails(false);

      navigate(`/editLearn/${learnId}`, {
        state: {
          id: learnId,
          learnId,
          userId: String(ownerUserId),
        },
      });
      return;
    }

    if (module === "do") {
      const doId = item?.do_id || item?.id;
      const ownerUserId =
        item?.user_id ||
        item?.userId ||
        detailsItem?.user_id ||
        detailsItem?.userId ||
        effectiveUserId;

      if (!doId) {
        alert("Missing do id.");
        return;
      }

      if (!ownerUserId) {
        alert("Missing user id.");
        return;
      }

      setShowDetails(false);

      navigate(`/editDo/${doId}`, {
        state: {
          id: doId,
          doId,
          userId: String(ownerUserId),
        },
      });
    }
  };
  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const common = {
        params: { userId: effectiveUserId, year, isAdmin: 1 },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      };

      const [learnRes, doRes] = await Promise.all([
        axios.get(ENDPOINT_ACTIVITIES, {
          ...common,
          params: { ...common.params, module: "learn" },
        }),
        axios.get(ENDPOINT_ACTIVITIES, {
          ...common,
          params: { ...common.params, module: "do" },
        }),
      ]);

      const learnData = Array.isArray(learnRes?.data?.data)
        ? learnRes.data.data.map((r) => ({
            id: r.learn_id ?? r.id ?? Math.random(),
            learn_id: r.learn_id ?? r.id ?? null,
            user_id: r.user_id ?? r.userId ?? effectiveUserId ?? "",
            date_from: r.actDateFr || r.activity_date_from || "",
            date_to: r.actDateTo || r.activity_date_to || "",
            title:
              r.activity_title ||
              r.course_title ||
              r.title ||
              r.activity_name ||
              "-",
            type_text: r.typeOfActivity || "",
            category_text: r.category || "",
            status_name: r.status_name || "Pending",
            notify: Number(r.notify || 0),
            remarks: pickRemarks(r),
            ctrCDDocs: Number(r.ctrCDDocs || 0),
            ctrVLDocs: Number(r.ctrVLDocs || 0),
            ctrSSTDocs: Number(r.ctrSSTDocs || 0),
            ctrPPDocs: Number(r.ctrPPDocs || 0),
            createdDate: r.createdDate || "",
            updatedDate: r.updatedDate || "",
            verifiedDate: r.verifiedDate || "",
          }))
          .filter((x) => String(x.status_name).toLowerCase() !== "draft")
        : [];

      const doData = Array.isArray(doRes?.data?.data)
        ? doRes.data.data.map((r) => ({
            id: r.do_id ?? r.id ?? Math.random(),
            do_id: r.do_id ?? r.id ?? null,
            date_from: r.actDateFr || r.activity_date_from || "",
            date_to: r.actDateTo || r.activity_date_to || "",
            status_name: r.status_name || "Pending",
            notify: Number(r.notify || 0),
            tour_conducted: Number(r.ctrPCDocs || 0) > 0,
            createdDate: r.createdDate || "",
            updatedDate: r.updatedDate || "",
            verifiedDate: r.verifiedDate || "",
            remarks: pickRemarks(r),
            ctrPNUMDocs: Number(r.ctrPNUMDocs || 0),
            ctrPCDocs: Number(r.ctrPCDocs || 0),
          }))
          .filter((x) => String(x.status_name).toLowerCase() !== "draft")
        : [];

      setLearn(learnData);
      setDoList(doData);
    } catch (e) {
      setErr("Failed to load activities. Please try again.");
      setLearn([]);
      setDoList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
    fetchCycleInfo();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleView = (item, module) => {
    setDetailsItem(item);
    setDetailsModule(module);
    setShowDetails(true);
  };

  const handleToggleLearnNotify = async (item) => {
    const learnId = toIntId(item.learn_id ?? item.id);
    if (!learnId) return;

    const nextNotify = item.notify ? 0 : 1;

    // optimistic update
    setLearn((prev) =>
      prev.map((x) =>
        String(x.id) === String(item.id) ? { ...x, notify: nextNotify } : x,
      ),
    );

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      await axios.post(
        ENDPOINT_TOGGLE_LEARN_NOTIFY,
        { learnId, notify: nextNotify },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          timeout: 20000,
        },
      );
    } catch (e) {
      // revert on failure
      setLearn((prev) =>
        prev.map((x) =>
          String(x.id) === String(item.id)
            ? { ...x, notify: item.notify }
            : x,
        ),
      );
      alert("Failed to update notify. Please try again.");
    }
  };

  const handleToggleDoNotify = async (item) => {
    const doId = toIntId(item.do_id ?? item.id);
    if (!doId) return;

    const nextNotify = item.notify ? 0 : 1;

    // optimistic update
    setDoList((prev) =>
      prev.map((x) =>
        String(x.id) === String(item.id) ? { ...x, notify: nextNotify } : x,
      ),
    );

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      await axios.post(
        ENDPOINT_TOGGLE_DO_NOTIFY,
        { doId, notify: nextNotify },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          timeout: 20000,
        },
      );
    } catch (e) {
      // revert on failure
      setDoList((prev) =>
        prev.map((x) =>
          String(x.id) === String(item.id)
            ? { ...x, notify: item.notify }
            : x,
        ),
      );
      alert("Failed to update notify. Please try again.");
    }
  };

  const handleSubmitChange = async ({ status, remarks }) => {
    if (!detailsItem || !detailsModule) return;
    setChangeBusy(true);

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const statusId = getStatusId(status);
      if (!statusId) {
        throw new Error(
          "Unknown status mapping. Please check STATUS_NAME_TO_ID.",
        );
      }

      let learnId, doId;
      if (detailsModule === "learn") {
        learnId = toIntId(detailsItem.learn_id ?? detailsItem.id);
        if (!learnId)
          throw new Error("Invalid learnId — cannot update status.");
      } else if (detailsModule === "do") {
        doId = toIntId(detailsItem.do_id ?? detailsItem.id);
        if (!doId) throw new Error("Invalid doId — cannot update status.");
      } else {
        throw new Error("Invalid module.");
      }

      const body = {
        statusId,
        module: detailsModule,
        remarks: remarks || "",
        ...(learnId ? { learnId } : {}),
        ...(doId ? { doId } : {}),
      };

      await axios.post(ENDPOINT_UPDATE_STATUS, body, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      const newLabel =
        status || STATUS_ID_TO_NAME[String(statusId)] || "Pending";

      if (detailsModule === "learn") {
        setLearn((prev) =>
          prev.map((x) =>
            String(x.id) === String(detailsItem.id)
              ? { ...x, status_name: newLabel, remarks: remarks || x.remarks }
              : x,
          ),
        );
      } else {
        setDoList((prev) =>
          prev.map((x) =>
            String(x.id) === String(detailsItem.id)
              ? { ...x, status_name: newLabel, remarks: remarks || x.remarks }
              : x,
          ),
        );
      }

      setDetailsItem((old) =>
        old
          ? { ...old, status_name: newLabel, remarks: remarks || old.remarks }
          : old,
      );
      setShowChange(false);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to update status. Please check the payload.";
      alert(msg);
      console.error("changeStatus failed:", e);
    } finally {
      setChangeBusy(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: "#f7f9fc" }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? handleLogout() : null
              }
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center"
            style={{ gap: 12 }}
          >
            <span
              role="button"
              aria-label="Go back"
              tabIndex={0}
              onClick={handleBack}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? handleBack() : null
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                background: "transparent",
                cursor: "pointer",
                outline: "none",
              }}
              className="back-icon"
            >
              <ArrowLeft size={18} color="#ffffff" />
            </span>
            <h4 className="m-0 fw-bold">LEARN + DO ACTIVITIES BOARD</h4>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        {profileLoading ? (
          <div className="mb-3 text-muted">
            <Spinner size="sm" className="me-2" /> Loading profile…
          </div>
        ) : profileErr ? (
          <div className="mb-3 text-danger">{profileErr}</div>
        ) : profile ? (
          <ProfileInfoBar info={profile} />
        ) : null}

        <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1F2937", marginRight: 4 }}>
            Filter by status
          </span>
          {["All", "Pending", "Approved", "Re-submit", "Rejected"].map((name) => {
            const active = statusFilter === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setStatusFilter(name)}
                style={{
                  border: `1px solid ${active ? "#183B72" : "#CBD5E1"}`,
                  background: active ? "#183B72" : "white",
                  color: active ? "white" : "#334155",
                  borderRadius: 999,
                  padding: "4px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {name} ({statusCount(name)})
              </button>
            );
          })}
        </div>

        <Card
          style={{
            background: PANEL_BG,
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
            borderRadius: 18,
          }}
        >
          <Card.Body
            style={{ background: "white", borderRadius: 16, padding: 16 }}
          >
            {loading ? (
              <div className="text-center py-5">
                <Spinner size="sm" className="me-2" /> Loading…
              </div>
            ) : err ? (
              <div className="text-center py-4 text-danger">{err}</div>
            ) : (
              <div
                className="d-grid"
                style={{
                  gridTemplateColumns: "1fr 1fr",
                  gap: 28,
                  minHeight: 420,
                }}
              >
                <div>
                  <ColumnHeader label="LEARN" count={visibleLearn.length} />
                  <div
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      paddingRight: 4,
                    }}
                  >
                    {visibleLearn.length ? (
                      visibleLearn.map((it) => (
                        <LearnCard
                          key={it.id}
                          item={it}
                          onView={handleView}
                          onToggleNotify={handleToggleLearnNotify}
                        />
                      ))
                    ) : (
                      <EmptyColumnState label="LEARN" />
                    )}
                  </div>
                </div>

                <div>
                  <ColumnHeader label="DO" count={visibleDo.length} />
                  <div
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      paddingRight: 4,
                    }}
                  >
                    {visibleDo.length ? (
                      visibleDo.map((it) => (
                        <DoCard
                          key={it.id}
                          item={it}
                          onView={handleView}
                          onToggleNotify={handleToggleDoNotify}
                        />
                      ))
                    ) : (
                      <EmptyColumnState label="DO" />
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card
          className="mt-4 shadow-sm overflow-hidden"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 18,
          }}
        >
          <div
            className="p-4 text-white"
            style={{ background: "linear-gradient(135deg, #183b72, #24519a)" }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.16)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Calendar3 size={22} color="#fff" />
              </div>
              <div>
                <h5 className="mb-1 fw-bold">TRANSITION PLAN</h5>
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  Display how many LEARN and DO activities are required for
                  each cycle.
                </div>
              </div>
            </div>
          </div>

          <Card.Body style={{ padding: 20 }}>
            {cycleLoading ? (
              <div className="text-center py-4">
                <Spinner size="sm" className="me-2" /> Loading transition
                plan…
              </div>
            ) : cycleErr ? (
              <div className="text-danger small">{cycleErr}</div>
            ) : cycleInfo?.transitionRows?.length ? (
              <div className="table-responsive">
                <Table
                  hover
                  className="align-middle mb-0"
                  style={{ minWidth: 640 }}
                >
                  <thead>
                    <tr style={{ background: "#f7f9fc" }}>
                      <th style={{ whiteSpace: "nowrap" }}>No</th>
                      <th style={{ whiteSpace: "nowrap" }}>
                        Licence Year End Date
                      </th>
                      <th style={{ whiteSpace: "nowrap" }}>LEARN Required</th>
                      <th style={{ whiteSpace: "nowrap" }}>DO Required</th>
                      <th style={{ whiteSpace: "nowrap" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycleInfo.transitionRows.map((row) => (
                      <tr key={row.key}>
                        <td style={{ fontWeight: 700 }}>{row.no}</td>
                        <td>{row.endFmt || "-"}</td>
                        <td>
                          <span
                            style={{
                              display: "inline-block",
                              borderRadius: 999,
                              padding: "6px 12px",
                              fontWeight: 700,
                              background: "#fff3cd",
                              color: "#7a5a00",
                            }}
                          >
                            {row.learnReq} Learn
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              display: "inline-block",
                              borderRadius: 999,
                              padding: "6px 12px",
                              fontWeight: 700,
                              background: "#ffe1dc",
                              color: "#a52813",
                            }}
                          >
                            {row.doReq} Do
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              display: "inline-block",
                              borderRadius: 999,
                              padding: "6px 12px",
                              fontWeight: 700,
                              fontSize: 13,
                              background: row.statusBg,
                              color: row.statusColor,
                            }}
                          >
                            {row.statusText}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-muted small">
                No transition plan found.
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Modals */}
      <DetailsModal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        baseItem={detailsItem}
        module={detailsModule}
        onOpenChangeStatus={() => setShowChange(true)}
        profile={profile}
        onEdit={handleEdit}
      />

      <ChangeStatusModal
        show={showChange}
        onHide={() => setShowChange(false)}
        currentStatus={detailsItem?.status_name}
        onSubmit={handleSubmitChange}
        busy={changeBusy}
      />
    </div>
  );
}
